import React, { useState, useEffect } from 'react';

interface UserProfile {
  id: number;
  last_name: string;
  first_name: string;
  last_name_kana: string;
  first_name_kana: string;
  gender: string;
  birth_date: string;
  phone_number: string;
  current_status: string;
  availability: string;
  desired_work_days_list: number[];
  current_annual_salary: number | null;
  prefecture: string;
  commute_time_preference: string;
}

interface UserProfilePageProps {
  initialUserProfile?: UserProfile | null;
  userEmail?: string;
  isUserSignedIn?: boolean;
}

const UserProfilePage: React.FC<UserProfilePageProps> = ({
  initialUserProfile = null,
  userEmail = '',
  isUserSignedIn = false
}) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(initialUserProfile);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [formData, setFormData] = useState<UserProfile | null>(initialUserProfile);

  // フォームデータを初期化
  useEffect(() => {
    if (userProfile) {
      setFormData(userProfile);
    }
  }, [userProfile]);

  // プロフィールを取得
  const fetchUserProfile = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/mypage/user_profile.json', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data.user_profile);
        setFormData(data.user_profile);
      } else {
        console.error('Failed to fetch user profile:', response.status);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // プロフィールを更新
  const handleUpdate = async () => {
    if (!formData) return;

    setIsLoading(true);
    setErrors([]);

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const response = await fetch('/mypage/user_profile', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken || ''
        },
        body: JSON.stringify({
          user_profile: formData
        })
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data.user_profile);
        setIsEditing(false);
        if (window.showGlobalFlash) {
          window.showGlobalFlash('プロフィールを更新しました', 'success', false);
        }
      } else {
        const errorData = await response.json();
        setErrors(errorData.errors || ['プロフィールの更新に失敗しました']);
      }
    } catch (error) {
      setErrors(['ネットワークエラーが発生しました']);
    } finally {
      setIsLoading(false);
    }
  };

  // フォームフィールドの更新
  const handleFieldChange = (field: keyof UserProfile, value: any) => {
    if (!formData) return;
    setFormData({ ...formData, [field]: value });
  };

  // 希望稼働日数の更新
  const handleDesiredWorkDaysChange = (days: number, checked: boolean) => {
    if (!formData) return;
    const currentDays = formData.desired_work_days_list || [];
    const newDays = checked
      ? [...currentDays, days].sort()
      : currentDays.filter(d => d !== days);
    setFormData({ ...formData, desired_work_days_list: newDays });
  };

  // ログインしていない場合の表示
  if (!isUserSignedIn) {
    return (
      <div className="container p-4">
        <div className="max-w-lg mx-auto text-center">
          <h1 className="h3 fw-bold mb-4">プロフィール</h1>
          <p className="text-muted mb-4">
            新規アカウントを登録すると、自分に合ったスキルや職種の案件を選択できます。
            自分に合った案件を見つけることができます。
          </p>
          <a href="/users/sign_up" className="btn btn-primary me-2">
            無料登録
          </a>
          <a href="/users/sign_in" className="btn btn-outline-primary">
            ログイン
          </a>
        </div>
      </div>
    );
  }

  // ローディング中の表示
  if (isLoading && !userProfile) {
    return (
      <div className="container p-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">読み込み中...</span>
          </div>
        </div>
      </div>
    );
  }

  // プロフィールが存在しない場合
  if (!userProfile && !isEditing) {
    return (
      <div className="container p-4">
        <h1 className="h3 fw-bold mb-4">プロフィール</h1>
        <p className="text-muted mb-4">プロフィールが登録されていません。</p>
        <a href="/registration/step1" className="btn btn-primary">
          プロフィールを登録する
        </a>
      </div>
    );
  }

  const displayProfile = isEditing ? formData : userProfile;
  if (!displayProfile) return null;

  return (
    <div className="container p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h3 fw-bold mb-0">プロフィール</h1>
        {!isEditing && (
          <button
            className="btn btn-primary"
            onClick={() => setIsEditing(true)}
          >
            編集する
          </button>
        )}
      </div>

      {errors.length > 0 && (
        <div className="alert alert-danger">
          <ul className="mb-0">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="row">
        {/* 個人情報 */}
        <div className="col-md-6 mb-4">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <strong>個人情報</strong>
            </div>
            <div className="card-body">
              {isEditing ? (
                <>
                  <div className="mb-3">
                    <label className="form-label">登録メールアドレス</label>
                    <p className="form-control-plaintext">{userEmail}</p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">姓</label>
                    <input
                      type="text"
                      className="form-control"
                      value={displayProfile.last_name || ''}
                      onChange={(e) => handleFieldChange('last_name', e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">名</label>
                    <input
                      type="text"
                      className="form-control"
                      value={displayProfile.first_name || ''}
                      onChange={(e) => handleFieldChange('first_name', e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">姓（カナ）</label>
                    <input
                      type="text"
                      className="form-control"
                      value={displayProfile.last_name_kana || ''}
                      onChange={(e) => handleFieldChange('last_name_kana', e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">名（カナ）</label>
                    <input
                      type="text"
                      className="form-control"
                      value={displayProfile.first_name_kana || ''}
                      onChange={(e) => handleFieldChange('first_name_kana', e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">性別</label>
                    <select
                      className="form-select"
                      value={displayProfile.gender || ''}
                      onChange={(e) => handleFieldChange('gender', e.target.value)}
                    >
                      <option value="male">男性</option>
                      <option value="female">女性</option>
                      <option value="undisclosed">回答しない</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">生年月日</label>
                    <input
                      type="date"
                      className="form-control"
                      value={displayProfile.birth_date || ''}
                      onChange={(e) => handleFieldChange('birth_date', e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">電話番号</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={displayProfile.phone_number || ''}
                      onChange={(e) => handleFieldChange('phone_number', e.target.value)}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-3">
                    <label className="form-label">登録メールアドレス</label>
                    <p className="form-control-plaintext">{userEmail}</p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">姓</label>
                    <p className="form-control-plaintext">{displayProfile.last_name}</p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">名</label>
                    <p className="form-control-plaintext">{displayProfile.first_name}</p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">姓（カナ）</label>
                    <p className="form-control-plaintext">{displayProfile.last_name_kana}</p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">名（カナ）</label>
                    <p className="form-control-plaintext">{displayProfile.first_name_kana}</p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">性別</label>
                    <p className="form-control-plaintext">
                      {displayProfile.gender === 'male' ? '男性' :
                       displayProfile.gender === 'female' ? '女性' : '回答しない'}
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">生年月日</label>
                    <p className="form-control-plaintext">{displayProfile.birth_date}</p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">電話番号</label>
                    <p className="form-control-plaintext">{displayProfile.phone_number}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* 就業状況・通勤関連 */}
        <div className="col-md-6 mb-4">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <strong>就業状況・通勤関連</strong>
            </div>
            <div className="card-body">
              {isEditing ? (
                <>
                  <div className="mb-3">
                    <label className="form-label">現在のステータス</label>
                    <select
                      className="form-select"
                      value={displayProfile.current_status || ''}
                      onChange={(e) => handleFieldChange('current_status', e.target.value)}
                    >
                      <option value="full_time">正社員</option>
                      <option value="freelance">フリーランス</option>
                      <option value="part_time">パートタイム</option>
                      <option value="contract">契約社員</option>
                      <option value="student">学生</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">就業可能時期</label>
                    <select
                      className="form-select"
                      value={displayProfile.availability || ''}
                      onChange={(e) => handleFieldChange('availability', e.target.value)}
                    >
                      <option value="immediate">即日</option>
                      <option value="within_one_month">1ヶ月以内</option>
                      <option value="within_three_months">3ヶ月以内</option>
                      <option value="within_six_months">6ヶ月以内</option>
                      <option value="within_one_year">1年以内</option>
                      <option value="considering">検討中</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">希望稼働日数/週</label>
                    <div className="form-check-group">
                      {[1, 2, 3, 4, 5].map((d) => (
                        <div key={d} className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`work_days_${d}`}
                            checked={(displayProfile.desired_work_days_list || []).includes(d)}
                            onChange={(e) => handleDesiredWorkDaysChange(d, e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor={`work_days_${d}`}>
                            週{d}日
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">現在の年収（万円）</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      value={displayProfile.current_annual_salary || ''}
                      onChange={(e) => handleFieldChange('current_annual_salary', e.target.value ? parseInt(e.target.value) : null)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">都道府県</label>
                    <select
                      className="form-select"
                      value={displayProfile.prefecture || ''}
                      onChange={(e) => handleFieldChange('prefecture', e.target.value)}
                    >
                      <option value="">選択してください</option>
                      <option value="tokyo">東京都</option>
                      <option value="osaka">大阪府</option>
                      {/* 他の都道府県も追加 */}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">通勤時間の希望</label>
                    <select
                      className="form-select"
                      value={displayProfile.commute_time_preference || ''}
                      onChange={(e) => handleFieldChange('commute_time_preference', e.target.value)}
                    >
                      <option value="within_30_minutes">30分以内</option>
                      <option value="within_1_hour">1時間以内</option>
                      <option value="within_1_hour_30_minutes">1時間30分以内</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-3">
                    <label className="form-label">現在のステータス</label>
                    <p className="form-control-plaintext">
                      {displayProfile.current_status === 'full_time' ? '正社員' :
                       displayProfile.current_status === 'freelance' ? 'フリーランス' :
                       displayProfile.current_status === 'part_time' ? 'パートタイム' :
                       displayProfile.current_status === 'contract' ? '契約社員' : '学生'}
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">就業可能時期</label>
                    <p className="form-control-plaintext">
                      {displayProfile.availability === 'immediate' ? '即日' :
                       displayProfile.availability === 'within_one_month' ? '1ヶ月以内' :
                       displayProfile.availability === 'within_three_months' ? '3ヶ月以内' :
                       displayProfile.availability === 'within_six_months' ? '6ヶ月以内' :
                       displayProfile.availability === 'within_one_year' ? '1年以内' : '検討中'}
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">希望稼働日数/週</label>
                    <p className="form-control-plaintext">
                      {(displayProfile.desired_work_days_list || []).map(d => `週${d}日`).join(', ') || '未設定'}
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">現在の年収（万円）</label>
                    <p className="form-control-plaintext">
                      {displayProfile.current_annual_salary ? `${displayProfile.current_annual_salary}万円` : '未設定'}
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">都道府県</label>
                    <p className="form-control-plaintext">{displayProfile.prefecture || '未設定'}</p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">通勤時間の希望</label>
                    <p className="form-control-plaintext">
                      {displayProfile.commute_time_preference === 'within_30_minutes' ? '30分以内' :
                       displayProfile.commute_time_preference === 'within_1_hour' ? '1時間以内' : '1時間30分以内'}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="text-end">
          <button
            className="btn btn-secondary me-2"
            onClick={() => {
              setIsEditing(false);
              setFormData(userProfile);
              setErrors([]);
            }}
          >
            キャンセル
          </button>
          <button
            className="btn btn-primary"
            onClick={handleUpdate}
            disabled={isLoading}
          >
            {isLoading ? '更新中...' : '更新する'}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserProfilePage;

